import React from 'react';
import { Field } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { createSlug } from '../../../util/urlHelpers';

import { FieldSelect } from '../../../components';

import css from './PriceVariantPicker.module.css';

const DEFAULT_PRICE_VARIANT_NAME = 'default-variant-name';

// Maps every known saved variant name (across all languages) to its translation key.
// When a listing is saved in one language, this lets us display the correct label
// in whatever language the viewer has selected.
const VARIANT_NAME_TO_INTL_KEY = {
  // English
  'per 24 hr': 'EditListingPricingForm.priceVariant.24hr',
  'per week': 'EditListingPricingForm.priceVariant.week',
  'per month': 'EditListingPricingForm.priceVariant.month',
  'per 3 days': 'EditListingPricingForm.priceVariant.3days',
  'per 2 weeks': 'EditListingPricingForm.priceVariant.2weeks',
  'per 3 weeks': 'EditListingPricingForm.priceVariant.3weeks',
  'per 6 months': 'EditListingPricingForm.priceVariant.6months',
  'per 1 year': 'EditListingPricingForm.priceVariant.1year',
  // Indonesian
  'per 24 jam': 'EditListingPricingForm.priceVariant.24hr',
  'per minggu': 'EditListingPricingForm.priceVariant.week',
  'per bulan': 'EditListingPricingForm.priceVariant.month',
  'per 3 hari': 'EditListingPricingForm.priceVariant.3days',
  'per 2 minggu': 'EditListingPricingForm.priceVariant.2weeks',
  'per 3 minggu': 'EditListingPricingForm.priceVariant.3weeks',
  'per 6 bulan': 'EditListingPricingForm.priceVariant.6months',
  'per 1 tahun': 'EditListingPricingForm.priceVariant.1year',
};

const getVariantLabel = (name, intl) => {
  const intlKey = VARIANT_NAME_TO_INTL_KEY[name];
  return intlKey ? intl.formatMessage({ id: intlKey }) : name;
};

const VariantNameMaybe = props => {
  const { className, priceVariant, intl } = props;
  if (!priceVariant?.name) return null;
  const label = getVariantLabel(priceVariant.name, intl);
  return (
    <div className={className}>
      <FormattedMessage
        id="PriceVariantPicker.onePriceVariantOnly"
        values={{ priceVariantName: label }}
      />
    </div>
  );
};

const FieldHidden = props => {
  const { name, ...rest } = props;
  return (
    <Field id={name} name={name} type="hidden" className={css.hidden} {...rest}>
      {fieldRenderProps => <input {...fieldRenderProps?.input} />}
    </Field>
  );
};

const PriceVariantPicker = props => {
  const intl = useIntl();
  const { priceVariants, onPriceVariantChange, disabled } = props;
  const hasMultiplePriceVariants = priceVariants?.length > 1;
  const hasOnePriceVariant = priceVariants?.length === 1;

  return hasMultiplePriceVariants ? (
    <FieldSelect
      name="priceVariantName"
      id="priceVariant"
      className={css.priceVariantFieldSelect}
      selectClassName={css.priceVariantSelect}
      label={intl.formatMessage({ id: 'PriceVariantPicker.priceVariantLabel' })}
      onChange={onPriceVariantChange}
      disabled={disabled}
      showLabelAsDisabled={disabled}
    >
      <option disabled value="" key="unselected">
        {intl.formatMessage({ id: 'PriceVariantPicker.priceVariantUnselected' })}
      </option>
      {priceVariants.map(pv => (
        <option value={pv.name} key={pv.name} data-slug={createSlug(pv.name)}>
          {getVariantLabel(pv.name, intl)}
        </option>
      ))}
    </FieldSelect>
  ) : hasOnePriceVariant ? (
    <>
      <VariantNameMaybe priceVariant={priceVariants?.[0]} className={css.priceVariantName} intl={intl} />
      <FieldHidden
        name="priceVariantName"
        format={value => {
          return value == null ? DEFAULT_PRICE_VARIANT_NAME : value;
        }}
        parse={value => {
          const response = value === DEFAULT_PRICE_VARIANT_NAME ? null : value;
          return response;
        }}
      />
    </>
  ) : null;
};

export default PriceVariantPicker;
